/* [PATCH opal-onboarding-E v1] Obvestilo o končanem Opal onboardingu — modal, ki ga
   mora uporabnik potrditi. Vgrajen v DashboardLayout (vidno povsod). Polling
   GET /opal-sync/ukaz/odprti vsakih 30 s (vzorec ObvestilaZvonec). Stranka dobi
   403 → polling se ustavi. Načrt: NACRT_opal_onboarding_en_klik_v2 §2 OBVESTILO.
   Endpointi: GET /opal-sync/ukaz/odprti · POST /opal-sync/ukaz/{id}/potrdi · /ponovi
*/
import React, { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, CheckCircle, Loader } from 'lucide-react';
import { api } from '../../lib/api';

type Ukaz = {
  id: number;
  company_id: string;
  podjetje: string | null;
  opal_baza: string;
  status: string;
  rezultat: any | null;
  napaka: string | null;
  potrjeno_ob: string | null;
  zacet_ob: string | null;
  zakljucen_ob: string | null;
};

const OSVEZI_MS = 30000;

function napakaBesedilo(e: any): string {
  const d = e?.response?.data?.detail;
  return typeof d === 'string' ? d : (e?.message || 'Neznana napaka');
}

export const OpalUkazModal: React.FC = () => {
  const [ukazi, setUkazi] = useState<Ukaz[]>([]);
  const [aktiven, setAktiven] = useState(true);
  const [izvajam, setIzvajam] = useState<string | null>(null);
  const [napaka, setNapaka] = useState<string | null>(null);

  const nalozi = useCallback(() => {
    if (!aktiven) return;
    api.get('/opal-sync/ukaz/odprti')
      .then((r: any) => setUkazi(r.data?.ukazi || []))
      .catch((e: any) => {
        const st = e?.response?.status;
        if (st === 403 || st === 401) setAktiven(false);   // stranka / brez seje: ne sprašuj več
      });
  }, [aktiven]);

  useEffect(() => {
    nalozi();
    const t = setInterval(() => { nalozi(); }, OSVEZI_MS);
    return () => clearInterval(t);
  }, [nalozi]);

  const koncan = ukazi.find((u) => (u.status === 'izveden' || u.status === 'napaka') && !u.potrjeno_ob) || null;
  if (!koncan) return null;

  const r = koncan.rezultat || {};
  const kartice = koncan.status === 'izveden' ? [
    { k: 'partnerji', naziv: 'Partnerji', st: r.partnerji?.vstavljeni, dod: r.partnerji?.s_trr != null ? `s TRR: ${r.partnerji.s_trr}` : null },
    { k: 'konti', naziv: 'Konti', st: (r.konti?.knjizni ?? 0) + (r.konti?.sinteticni ?? 0), dod: r.konti ? `knjižni ${r.konti.knjizni ?? 0} · sintetični ${r.konti.sinteticni ?? 0}` : null },
    { k: 'sm', naziv: 'Stroškovna mesta', st: r.stroskovna_mesta?.vstavljeni, dod: null },
    { k: 'projekti', naziv: 'Projekti', st: (r.projekti?.vstavljeni ?? 0) + (r.projekti?.ozivljeni ?? 0), dod: null },
    { k: 'seed', naziv: 'Učni primeri (prejeti)', st: r.seed?.seed_vstavljeni ?? r.seed?.vstavljeni, dod: r.seed?.dokumenti_obdelani != null ? `iz dokumentov: ${r.seed.dokumenti_obdelani}` : null },
    { k: 'seed_ir', naziv: 'Učni primeri (izdani)', st: r.seed_ir?.seed_vstavljeni ?? r.seed_ir?.vstavljeni, dod: null },
  ] : [];

  const potrdi = async () => {
    setIzvajam('potrdi'); setNapaka(null);
    try {
      await api.post(`/opal-sync/ukaz/${koncan.id}/potrdi`);
      setUkazi((s) => s.filter((u) => u.id !== koncan.id));
    } catch (e: any) { setNapaka(napakaBesedilo(e)); } finally { setIzvajam(null); }
  };
  const ponovi = async () => {
    setIzvajam('ponovi'); setNapaka(null);
    try {
      await api.post(`/opal-sync/ukaz/${koncan.id}/ponovi`);
      setUkazi((s) => s.filter((u) => u.id !== koncan.id));
    } catch (e: any) { setNapaka(napakaBesedilo(e)); } finally { setIzvajam(null); }
  };

  const ok = koncan.status === 'izveden';
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 p-4">
      <div className={`w-full max-w-lg bg-white dark:bg-gray-900 rounded-2xl shadow-2xl border overflow-hidden ${ok ? 'border-green-200 dark:border-green-900' : 'border-red-200 dark:border-red-900'}`}>
        <div className={`flex items-center gap-3 px-5 py-4 border-b ${ok ? 'bg-green-50 dark:bg-green-950/40 border-green-200 dark:border-green-900' : 'bg-red-50 dark:bg-red-950/40 border-red-200 dark:border-red-900'}`}>
          {ok ? <CheckCircle className="w-6 h-6 text-green-600 dark:text-green-400 shrink-0" /> : <AlertTriangle className="w-6 h-6 text-red-600 dark:text-red-400 shrink-0" />}
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-gray-900 dark:text-white">{ok ? 'Uvoz iz Opala končan' : 'Uvoz iz Opala NI uspel'}</h3>
            <div className="text-xs text-gray-500 dark:text-gray-400 truncate">{koncan.podjetje || koncan.company_id} · baza {koncan.opal_baza} · ukaz #{koncan.id}</div>
          </div>
        </div>

        <div className="px-5 py-4 space-y-3 text-sm">
          {ok && (
            <>
              <div className="grid grid-cols-2 gap-3">
                {kartice.map((c) => (
                  <div key={c.k} className="p-3 rounded-xl border border-gray-200 dark:border-gray-800">
                    <div className="text-xs text-gray-500 dark:text-gray-400">{c.naziv}</div>
                    <div className="text-2xl font-bold text-gray-900 dark:text-white">{c.st ?? 0}</div>
                    {c.dod && <div className="text-xs text-gray-400 dark:text-gray-500">{c.dod}</div>}
                  </div>
                ))}
              </div>
              <div className="text-xs text-green-700 dark:text-green-300">Sinhronizacija s to Opalovo bazo je vklopljena — šifranti se od zdaj usklajujejo samodejno.</div>
            </>
          )}
          {!ok && (
            <div className="p-3 rounded-xl bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900 text-red-700 dark:text-red-300 text-xs whitespace-pre-wrap">{koncan.napaka || 'Brez opisa napake'}</div>
          )}
          {napaka && <div className="text-xs text-red-600 dark:text-red-400">{napaka}</div>}
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-gray-200 dark:border-gray-800">
          {!ok && (
            <button onClick={ponovi} disabled={!!izvajam} className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-xl border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-200 disabled:opacity-50">
              {izvajam === 'ponovi' && <Loader className="w-4 h-4 animate-spin" />} Ponovi
            </button>
          )}
          <button onClick={potrdi} disabled={!!izvajam} className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-xl bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50">
            {izvajam === 'potrdi' && <Loader className="w-4 h-4 animate-spin" />} Razumem
          </button>
        </div>
      </div>
    </div>
  );
};

export default OpalUkazModal;
